import { useCallback, useContext, useEffect, useState } from 'react';
import { Link, useLocation, useOutletContext, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import NewSideBar from '../../components/Navigation/NewSideBar';
import FullTable from '../../components/Table/FullTable';
import { LayoutContext } from '../../utils/contexts/LayoutContext';
import { useDebounce } from '../../utils/hooks/useDebounce';
import { getQuestions } from '../../services/Interviews/questionService';
import { postInterviewQuestion } from '../../services/Interviews/interviewQuestionsService';

const PAGE_SIZE = 20;

export default function MyQuestionspage({ embedded = false }) {
  const { uuid } = useParams();
  const { state } = useLocation();
  const outletContext = useOutletContext();
  const interviewName = outletContext?.interview?.name || state?.interviewName || 'this interview';
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const setPadding = useContext(LayoutContext)?.setPadding;

  useEffect(() => {
    if (embedded || !setPadding) return undefined;
    setPadding('p-0');
    return () => setPadding('p-4');
  }, [embedded, setPadding]);

  const loadQuestions = useCallback((signal) => {
    setLoading(true);
    setError('');
    return getQuestions({
      params: { question: debouncedSearch || undefined, page, page_size: PAGE_SIZE, interview_not: uuid, for_interview: uuid },
      signal,
    }).then((data) => {
      setItems(data?.results || []);
      setCount(data?.count || 0);
    }).catch((requestError) => {
      if (!signal?.aborted) setError(requestError?.response?.data?.detail || 'The question bank could not be loaded.');
    }).finally(() => {
      if (!signal?.aborted) setLoading(false);
    });
  }, [debouncedSearch, page, uuid]);

  useEffect(() => {
    const controller = new AbortController();
    loadQuestions(controller.signal);
    return () => controller.abort();
  }, [loadQuestions]);
  useEffect(() => setPage(1), [debouncedSearch]);

  const handleAddQuestions = async () => {
    if (!selectedKeys.size) return;
    setAdding(true);
    const ids = Array.from(selectedKeys);
    const results = await Promise.allSettled(ids.map((question) => postInterviewQuestion({ interview: uuid, question })));
    const successfulIds = ids.filter((_, index) => results[index].status === 'fulfilled');
    const failed = ids.length - successfulIds.length;
    setItems((current) => current.filter((item) => !successfulIds.includes(item.uuid)));
    setCount((current) => Math.max(0, current - successfulIds.length));
    setSelectedKeys(new Set());
    setAdding(false);
    if (successfulIds.length) toast.success(`Added ${successfulIds.length} question${successfulIds.length === 1 ? '' : 's'} to ${interviewName}.`);
    if (failed) toast.error(`${failed} selected question${failed === 1 ? '' : 's'} could not be added.`);
    outletContext?.refreshInterview?.();
  };

  const columns = [
    {
      key: 'question', label: 'Question', className: 'min-w-[20rem]',
      render: (item) => <div><p className="font-semibold text-gray-950 dark:text-white">{item.name || 'Untitled question'}</p><p className="mt-1 max-w-2xl text-sm text-gray-500">{item.body}</p></div>,
    },
    {
      key: 'skills', label: 'Managed skills', className: 'min-w-[12rem]',
      render: (item) => item.skills?.length ? <div className="flex flex-wrap gap-1.5">{item.skills.map((skill) => <span key={skill.uuid} className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-200">{skill.name}</span>)}</div> : <span className="text-xs text-gray-400">No managed skills</span>,
    },
    {
      key: 'followups', label: 'Follow-ups', className: 'w-24 text-center',
      render: (item) => (item.type?.code === 'DYNAMIC_FOLLOWUP' ? 'Adaptive' : '—'),
    },
  ];
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className={`flex h-full ${embedded ? '' : 'bg-gray-50 dark:bg-gray-900'}`}>
      {!embedded && <NewSideBar />}
      <main className={`w-full overflow-y-auto ${embedded ? '' : 'p-6 lg:p-8'}`}>
        <div className="mx-auto max-w-7xl space-y-5">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <Link to={`/organization/interviews/${uuid}/questions`} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600"><ArrowLeftIcon className="h-4 w-4" />Back to interview questions</Link>
              <h1 className="mt-3 text-2xl font-bold">Add questions from the bank</h1>
              <p className="mt-1 text-sm text-gray-500">Choose reusable questions for {interviewName}. Already-added questions are excluded automatically.</p>
            </div>
            <Link to="/question/create" state={{ returnTo: `/organization/interviews/${uuid}/questions/add`, interviewUuid: uuid }} className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"><PlusIcon className="h-5 w-5" />Create a compatible question</Link>
          </div>

          <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-col gap-3 border-b border-gray-200 p-5 dark:border-gray-700 md:flex-row md:items-end">
              <label className="flex-1 text-sm font-medium">Search the question bank<input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by title or question text" className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 dark:border-gray-600 dark:bg-gray-900" /></label>
              <button type="button" onClick={handleAddQuestions} disabled={!selectedKeys.size || adding} className="rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40">{adding ? 'Adding…' : `Add selected${selectedKeys.size ? ` (${selectedKeys.size})` : ''}`}</button>
            </div>
            {error && <div role="alert" className="m-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-800"><p>{error}</p><button type="button" onClick={() => loadQuestions()} className="mt-2 font-semibold underline">Try again</button></div>}
            <div className="overflow-x-auto p-4"><FullTable data={items} columns={columns} showOrderNumber={false} loading={loading} enableSearch={false} enableCheckbox enableDnd={false} emptyMessage={search ? 'No available questions match your search.' : 'Every bank question has already been added, or the bank is empty.'} onSelectionChange={(ids) => setSelectedKeys(new Set(ids))} /></div>
            <div className="flex flex-col justify-between gap-3 border-t border-gray-200 p-4 text-sm dark:border-gray-700 sm:flex-row sm:items-center"><span className="text-gray-500">{count} available question{count === 1 ? '' : 's'}</span><nav aria-label="Question bank pagination" className="flex items-center gap-2"><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Previous</button><span>Page {page} of {pageCount}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Next</button></nav></div>
          </section>
        </div>
      </main>
    </div>
  );
}
